import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidV4 } from 'uuid';
import { Container, Card, CardBody, Button } from 'reactstrap';
import browserLocation from '../../utils/browserLocation';
import { buildClientErrorPayload, reportClientError, ERROR_BOUNDARY_SOURCE } from '../../utils/clientErrorReporter';
import { recordJsError } from '../../utils/feedbackDiagnostics';
import { resolveDefaultLandingUrlAfterAuth } from '../login/postAuthDefaultLanding';
import { getItem, setItem } from '../../utils/localStorage';
const FALLBACK_LANDING_URL = '/file-cabinet';

// Survives a reload so we can detect a render error that keeps recurring and
// avoid an infinite refresh loop on a deterministic boot-time crash.
const REFRESH_KEY = 'errorBoundaryRefreshCount';
const MAX_REFRESH = 2;
const COPIED_RESET_MS = 2000;
class ErrorBoundary extends Component {
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      info: null,
      reference: null,
      copyStatus: 'idle',
      refreshExhausted: false
    };
    this.handleRefresh = this.handleRefresh.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.handleCopyDiagnostics = this.handleCopyDiagnostics.bind(this);
  }
  componentDidCatch(error, info) {
    // Generate the reference once and reuse it for both the POST report and the
    // copied diagnostics so support can correlate the two.
    const reference = uuidV4().toUpperCase();
    this.setState({
      info,
      reference
    });
    // No centralized telemetry/logError utility exists in web yet, and the
    // shared logger only logs in dev/docker. console.error is the only trace
    // available in production when the user is not authenticated.
    /* eslint-disable no-console */
    console.error('ErrorBoundary caught an error:', error, info);
    /* eslint-enable no-console */
    reportClientError(error, info, {
      reference,
      source: ERROR_BOUNDARY_SOURCE
    });
    // Also keep a short copy in the feedback diagnostics buffer so a later problem report
    // can include the crash without asking the user to paste anything.
    recordJsError({
      type: 'errorBoundary',
      message: (error === null || error === void 0 ? void 0 : error.message) || 'Render error',
      stack: (error === null || error === void 0 ? void 0 : error.stack) || ''
    });
  }
  componentWillUnmount() {
    if (this.copyTimer) {
      global.clearTimeout(this.copyTimer);
    }
  }
  handleRefresh() {
    const parsed = Number(getItem(REFRESH_KEY, '0'));
    const count = Number.isFinite(parsed) ? parsed : 0;
    if (count >= MAX_REFRESH) {
      this.setState({
        refreshExhausted: true
      });
      return;
    }
    setItem(REFRESH_KEY, String(count + 1));
    browserLocation.reload();
  }

  // eslint-disable-next-line class-methods-use-this
  async handleContinue() {
    let target = FALLBACK_LANDING_URL;
    try {
      const resolved = await resolveDefaultLandingUrlAfterAuth();
      if (resolved) {
        target = resolved;
      }
    } catch (_unused) {
      // Fall back to the file cabinet if the landing URL cannot be resolved.
    }
    // Hard navigation avoids re-rendering the crashing subtree on the current
    // route before the landing URL is reached.
    browserLocation.assign(target);
  }
  handleCopyDiagnostics() {
    var _global$navigator$cli;
    const {
      error,
      info,
      reference
    } = this.state;
    const payload = buildClientErrorPayload(error, info, {
      reference
    });
    if ((_global$navigator$cli = global.navigator.clipboard) !== null && _global$navigator$cli !== void 0 && _global$navigator$cli.writeText) {
      global.navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(() => {
        this.setCopyStatus('copied');
      }).catch(() => {
        this.setCopyStatus('failed');
      });
    } else {
      this.setCopyStatus('failed');
    }
  }
  setCopyStatus(copyStatus) {
    this.setState({
      copyStatus
    });
    if (this.copyTimer) {
      global.clearTimeout(this.copyTimer);
    }
    this.copyTimer = global.setTimeout(() => {
      this.setState({
        copyStatus: 'idle'
      });
    }, COPIED_RESET_MS);
  }
  render() {
    const {
      hasError,
      copyStatus,
      refreshExhausted
    } = this.state;
    const {
      children
    } = this.props;
    if (hasError) {
      let copyLabel = 'Copy diagnostics';
      if (copyStatus === 'copied') {
        copyLabel = 'Copied!';
      } else if (copyStatus === 'failed') {
        copyLabel = 'Copy failed';
      }
      return /*#__PURE__*/React.createElement(Container, {
        className: "mt-5"
      }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(CardBody, {
        className: "text-center p-5"
      }, /*#__PURE__*/React.createElement("h2", null, "Something went wrong"), /*#__PURE__*/React.createElement("p", {
        className: "mt-3"
      }, refreshExhausted ? `Refreshing did not resolve the problem. Please copy the
                     diagnostics below and contact support.` : `An unexpected error occurred. Please try refreshing the
                     page. If the problem persists, contact support and include
                     the diagnostics copied below.`), /*#__PURE__*/React.createElement("div", {
        className: "mt-4"
      }, /*#__PURE__*/React.createElement(Button, {
        color: "primary",
        className: "mr-3",
        disabled: refreshExhausted,
        onClick: this.handleRefresh
      }, "Refresh"), /*#__PURE__*/React.createElement(Button, {
        color: "primary",
        outline: true,
        className: "mr-3",
        onClick: this.handleContinue
      }, "Continue"), /*#__PURE__*/React.createElement(Button, {
        color: "secondary",
        onClick: this.handleCopyDiagnostics
      }, copyLabel)))));
    }
    return children;
  }
}
ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};
export default ErrorBoundary;